import { useState } from 'react';
import { toast } from 'sonner';
import {
  DVF_PROPERTY_TYPES,
  WATCHLIST_STAGES,
  type AnalysisItem,
  type Commune,
  type DvfPropertyType,
  type SectorAnalysis,
  type SectorThresholds,
  type StrategyVerdict,
  type WatchlistItem,
  type WatchlistStage,
} from '@app/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@/lib/api';
import { formatRatio } from '@/lib/format';
import { CommuneSearch } from '@/features/market/CommuneSearch';
import { useAddToWatchlist, useSectorAnalysis, useUpdateWatchlistItem, useWatchlist } from './queries';

const STAGE_LABELS: Record<WatchlistStage, string> = {
  sweep: 'Balayage',
  shortlist: 'Présélection',
  comparison: 'Comparaison',
  field: 'Terrain',
};

/** Repères de la méthode : dégrossir large, présélectionner 10 à 15, comparer 2 à 5, puis le terrain. */
const STAGE_HINTS: Record<WatchlistStage, string> = {
  sweep: 'Communes repérées, sans tri.',
  shortlist: 'Visez 10 à 15.',
  comparison: 'Visez 2 à 5 ; l’analyse en compare 5 au plus.',
  field: 'À aller voir sur place.',
};

const MAX_COMPARED = 5;

const VERDICT_LABELS: Record<StrategyVerdict, string> = {
  feasible: 'Envisageable',
  difficult: 'Difficile',
  'not-evaluable': 'Non évalué',
};

const VERDICT_STYLE: Record<StrategyVerdict, string> = {
  feasible: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
  difficult: 'bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200',
  'not-evaluable': 'bg-muted text-muted-foreground',
};

const eurosFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const euros = (value: number | null) => (value === null ? 'n.a' : eurosFormatter.format(value));
const rentFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });

function WatchlistEntry({
  item,
  selected,
  selectionFull,
  onToggle,
}: {
  item: WatchlistItem;
  selected: boolean;
  selectionFull: boolean;
  onToggle: () => void;
}) {
  const update = useUpdateWatchlistItem();
  const stageIndex = WATCHLIST_STAGES.indexOf(item.stage);
  const move = (stage: WatchlistStage) =>
    update.mutate({ id: item.id, update: { stage } }, { onError: (e) => toast.error(apiErrorMessage(e, 'Échec du déplacement')) });

  return (
    <li className="flex flex-col gap-2 rounded-md border border-border p-2 text-sm">
      <div className="flex items-start justify-between gap-2">
        <label className="flex items-center gap-2 font-medium">
          <input
            type="checkbox"
            checked={selected}
            disabled={!selected && selectionFull}
            onChange={onToggle}
            aria-label={`Comparer ${item.communeName}`}
          />
          <span>
            {item.communeName} <span className="font-normal text-muted-foreground">({item.department || item.inseeCode})</span>
          </span>
        </label>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            disabled={stageIndex === 0 || update.isPending}
            aria-label={`Reculer ${item.communeName} d’une étape`}
            onClick={() => move(WATCHLIST_STAGES[stageIndex - 1]!)}
          >
            ←
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={stageIndex === WATCHLIST_STAGES.length - 1 || update.isPending}
            aria-label={`Avancer ${item.communeName} d’une étape`}
            onClick={() => move(WATCHLIST_STAGES[stageIndex + 1]!)}
          >
            →
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={update.isPending}
            aria-label={`Écarter ${item.communeName}`}
            onClick={() =>
              update.mutate(
                { id: item.id, update: { archived: true } },
                { onSuccess: () => toast.success(`${item.communeName} écartée`) },
              )
            }
          >
            Écarter
          </Button>
        </div>
      </div>
      <Input
        defaultValue={item.note}
        placeholder="Note"
        aria-label={`Note pour ${item.communeName}`}
        maxLength={1000}
        onBlur={(event) => {
          const note = event.target.value.trim();
          if (note !== item.note) update.mutate({ id: item.id, update: { note } });
        }}
      />
    </li>
  );
}

const ROWS: { label: string; get: (analysis: SectorAnalysis) => string }[] = [
  { label: 'Année de référence', get: (a) => (a.referenceYear === null ? 'n.a' : String(a.referenceYear)) },
  { label: 'Prix bas (25 %) €/m²', get: (a) => euros(a.p25PricePerM2) },
  { label: 'Prix médian €/m²', get: (a) => euros(a.referencePricePerM2) },
  { label: 'Prix haut (75 %) €/m²', get: (a) => euros(a.p75PricePerM2) },
  {
    label: 'Loyer €/m²/mois',
    get: (a) => (a.rent === null ? 'n.a' : `${rentFormatter.format(a.rent.rentPerM2Cents / 100)}${a.rent.reliable ? '' : ' ⚠'}`),
  },
  { label: 'Rendement brut', get: (a) => formatRatio(a.grossYield) },
  { label: 'Croissance des prix / an', get: (a) => formatRatio(a.annualGrowth) },
];

function VerdictBadge({ verdict }: { verdict: StrategyVerdict }) {
  return <Badge className={VERDICT_STYLE[verdict]}>{VERDICT_LABELS[verdict]}</Badge>;
}

function ComparisonTable({ items, thresholds }: { items: AnalysisItem[]; thresholds: SectorThresholds }) {
  const ok = items.filter((item) => item.analysis !== null);
  const strategies = ok[0]?.analysis?.strategies ?? [];

  return (
    <div className="flex flex-col gap-4">
      {items.filter((item) => item.status === 'error').map((item) => (
        <p key={item.inseeCode} className="text-sm text-destructive">
          Commune {item.inseeCode} : {item.error}
        </p>
      ))}

      {ok.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="p-2 text-left font-medium text-muted-foreground">Ligne</th>
                {ok.map((item) => (
                  <th key={item.inseeCode} className="p-2 text-right font-medium">
                    {item.analysis!.communeName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.label} className="border-b border-border/50">
                  <td className="p-2 text-muted-foreground">{row.label}</td>
                  {ok.map((item) => (
                    <td key={item.inseeCode} className="p-2 text-right tabular-nums">
                      {row.get(item.analysis!)}
                    </td>
                  ))}
                </tr>
              ))}
              {strategies.map((strategy) => (
                <tr key={strategy.strategy} className="border-b border-border/50 align-top">
                  <td className="p-2 text-muted-foreground">{strategy.label}</td>
                  {ok.map((item) => {
                    const result = item.analysis!.strategies.find((s) => s.strategy === strategy.strategy)!;
                    return (
                      <td key={item.inseeCode} className="p-2 text-right">
                        <VerdictBadge verdict={result.verdict} />
                        <div className="mt-1 text-xs text-muted-foreground">{result.reason}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ok.map((item) => (
        <div key={item.inseeCode} className="text-xs text-muted-foreground">
          <div className="font-medium">{item.analysis!.communeName}</div>
          <ul className="list-disc pl-5">
            {item.analysis!.caveats.map((caveat) => (
              <li key={caveat}>{caveat}</li>
            ))}
          </ul>
        </div>
      ))}

      <p className="text-xs text-muted-foreground">
        Seuils v1, à affiner : rendement brut ≥ {formatRatio(thresholds.minGrossYield)} pour la revente à
        investisseurs, prix médian ≥ {euros(thresholds.minRenovationPricePerM2)}/m² pour la rénovation-revente. Ils
        sont nationaux et non justifiés (M-D6) : à confronter au terrain, pas à suivre à la lettre.
      </p>
    </div>
  );
}

export function SectorBoard() {
  const watchlist = useWatchlist();
  const add = useAddToWatchlist();
  const [selected, setSelected] = useState<string[]>([]);
  const [propertyType, setPropertyType] = useState<DvfPropertyType>('Appartement');
  const [analyzed, setAnalyzed] = useState<string[] | null>(null);
  const analysis = useSectorAnalysis(analyzed, propertyType);

  const items = watchlist.data ?? [];
  const toggle = (code: string) =>
    setSelected((current) => (current.includes(code) ? current.filter((c) => c !== code) : [...current, code]));

  const handleAdd = (commune: Commune) =>
    add.mutate(
      { inseeCode: commune.code, communeName: commune.name, department: commune.department },
      {
        onSuccess: () => toast.success(`${commune.name} ajoutée au balayage`),
        onError: (error) => toast.error(apiErrorMessage(error, 'Ajout impossible')),
      },
    );

  return (
    <div className="flex flex-col gap-6">
      <Alert>
        <AlertDescription>
          Entonnoir de sélection : balayez large, présélectionnez 10 à 15 communes, comparez-en 2 à 5, puis allez sur
          le terrain. L’outil rend le dégrossissage rapide et répétable ; il ne remplace pas la visite.
        </AlertDescription>
      </Alert>

      <CommuneSearch onSelect={handleAdd} />

      {watchlist.isPending && <p className="text-muted-foreground">Chargement…</p>}
      {watchlist.isError && <p className="text-destructive">{apiErrorMessage(watchlist.error, 'Liste indisponible')}</p>}

      {watchlist.data && (
        <>
          <p className="text-sm text-muted-foreground">
            {items.length} commune(s) suivie(s) ·{' '}
            {WATCHLIST_STAGES.map((stage) => `${items.filter((item) => item.stage === stage).length} ${STAGE_LABELS[stage].toLowerCase()}`).join(' · ')}
          </p>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            {WATCHLIST_STAGES.map((stage) => {
              const inStage = items.filter((item) => item.stage === stage);
              return (
                <Card key={stage}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {STAGE_LABELS[stage]} <span className="font-normal text-muted-foreground">({inStage.length})</span>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">{STAGE_HINTS[stage]}</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="flex flex-col gap-2">
                      {inStage.map((item) => (
                        <WatchlistEntry
                          key={item.id}
                          item={item}
                          selected={selected.includes(item.inseeCode)}
                          selectionFull={selected.length >= MAX_COMPARED}
                          onToggle={() => toggle(item.inseeCode)}
                        />
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-2" role="group" aria-label="Type de bien">
              {DVF_PROPERTY_TYPES.map((type) => (
                <Button
                  key={type}
                  size="sm"
                  variant={type === propertyType ? 'default' : 'outline'}
                  aria-pressed={type === propertyType}
                  onClick={() => setPropertyType(type)}
                >
                  {type}
                </Button>
              ))}
            </div>
            <Button
              disabled={selected.length === 0 || analysis.isFetching}
              onClick={() => setAnalyzed([...selected])}
            >
              {analysis.isFetching ? 'Analyse…' : `Analyser la sélection (${selected.length}/${MAX_COMPARED})`}
            </Button>
            <span className="text-sm text-muted-foreground">
              Cochez jusqu’à {MAX_COMPARED} communes. La première analyse d’une commune prend quelques secondes.
            </span>
          </div>
        </>
      )}

      {analysis.isError && <p className="text-destructive">{apiErrorMessage(analysis.error, 'Analyse impossible')}</p>}
      {analysis.data && <ComparisonTable items={analysis.data.items} thresholds={analysis.data.thresholds} />}
    </div>
  );
}
